import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from '@components';
import type { SelectOption } from '@components/forms/Select/Select';
import type { HttpTool, ScenarioConfig, ScenarioIssue, ValueSource } from './types';
import { isDerivedFact, issuesAt, newValueSource } from './scenarioDraft';
import { FieldLabel, IssueList } from './ui';

const SYSTEM_KEYS = ['caller_number', 'conversation_id', 'today', 'now'];

interface JsonLiteralFieldProps {
  value: unknown;
  onChange: (value: unknown) => void;
  invalidLabel: string;
  hasError?: boolean;
  ariaLabel?: string;
}

/** Edits an arbitrary JSON literal; strings must be quoted, e.g. "ACCEPTED". */
export const JsonLiteralField: React.FC<JsonLiteralFieldProps> = ({
  value,
  onChange,
  invalidLabel,
  hasError,
  ariaLabel,
}) => {
  const serialized = JSON.stringify(value ?? null);
  const [draft, setDraft] = useState(serialized);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setDraft(current => {
      try {
        if (JSON.stringify(JSON.parse(current)) === serialized) return current;
      } catch {
        // fall through to the serialized value
      }
      return serialized;
    });
    setInvalid(false);
  }, [serialized]);

  return (
    <div>
      <Input
        size="sm"
        className="font-mono"
        aria-label={ariaLabel}
        value={draft}
        state={invalid || hasError ? 'error' : 'default'}
        onChange={e => {
          setDraft(e.target.value);
          try {
            onChange(JSON.parse(e.target.value));
            setInvalid(false);
          } catch {
            setInvalid(true);
          }
        }}
      />
      {invalid && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{invalidLabel}</p>}
    </div>
  );
};

const ALL_SOURCES: ValueSource['source'][] = ['fact', 'input', 'system', 'constant'];

interface ValueSourceEditorProps {
  config: ScenarioConfig;
  /** The tool whose inputs can be used; without it there is no input source. */
  tool?: HttpTool;
  value: ValueSource;
  onChange: (value: ValueSource) => void;
  issues: ScenarioIssue[];
  pointer: string;
  allowLists?: boolean;
  /** Cases of a derived fact cannot read other derived facts. */
  allowDerived?: boolean;
  sources?: ValueSource['source'][];
  label?: string;
  /** Further entries of the source list, e.g. a moment in conditions; picking one calls onExtraSource. */
  extraSources?: SelectOption[];
  onExtraSource?: (value: string) => void;
}

export const ValueSourceEditor: React.FC<ValueSourceEditorProps> = ({
  config,
  tool,
  value,
  onChange,
  issues,
  pointer,
  allowLists = true,
  allowDerived = true,
  sources = ALL_SOURCES,
  label,
  extraSources = [],
  onExtraSource,
}) => {
  const { t } = useTranslation();
  const keyOptions = (() => {
    switch (value.source) {
      case 'input':
        return (tool?.inputs ?? []).map(i => ({ value: i.name, label: i.name }));
      case 'fact':
        return config.facts
          .filter(f => (allowLists || f.type !== 'list') && (allowDerived || !isDerivedFact(f)))
          .map(f => ({ value: f.name, label: `${f.name} (${f.type})` }));
      case 'system':
        return SYSTEM_KEYS.map(key => ({ value: key, label: t(`agents.scenario.system.${key}`) }));
      default:
        return [];
    }
  })();
  const currentKey = value.source === 'constant' ? '' : value.key;
  const knownKey = keyOptions.some(o => o.value === currentKey);

  return (
    <div>
      <FieldLabel label={label ?? t('agents.scenario.valueSource')} />
      <div className="grid grid-cols-[130px_1fr] gap-2">
        <Select
          size="sm"
          value={value.source}
          options={[
            ...sources.map(source => ({ value: source, label: t(`agents.scenario.sources.${source}`) })),
            ...extraSources,
          ]}
          onChange={selected => {
            const picked = Array.isArray(selected) ? selected[0] : selected;
            if (sources.includes(picked as ValueSource['source'])) {
              onChange(newValueSource(picked as ValueSource['source'], value));
            } else {
              onExtraSource?.(picked);
            }
          }}
        />
        {value.source === 'constant' ? (
          <JsonLiteralField
            value={value.value}
            onChange={literal => onChange({ source: 'constant', value: literal })}
            invalidLabel={t('agents.scenario.invalidJsonLiteral')}
            hasError={issuesAt(issues, `${pointer}/value`).length > 0}
            ariaLabel={t('agents.scenario.constantValue')}
          />
        ) : (
          <Select
            size="sm"
            value={currentKey}
            placeholder={t('agents.scenario.selectPlaceholder')}
            error={issuesAt(issues, `${pointer}/key`).length > 0}
            options={knownKey || !currentKey ? keyOptions : [...keyOptions, { value: currentKey, label: `${currentKey} (?)` }]}
            onChange={selected =>
              onChange({ source: value.source, key: Array.isArray(selected) ? selected[0] : selected })
            }
          />
        )}
      </div>
      <IssueList issues={issues} pointer={pointer} />
      <IssueList issues={issues} pointer={`${pointer}/key`} />
      <IssueList issues={issues} pointer={`${pointer}/value`} />
    </div>
  );
};
